import { useState, useCallback } from "react";
import { spaceService } from "@/services/space-service";
import { privateOfficeService } from "@/services/private-office-service";
import { InventoryPrivateRoomSummary } from "@/features/private-room/list/adapters/get-privaterooms/get-privaterooms.types.ts";

type SpacePO = {
  id: string;
  search_id?: number;
  label: string;
  spaceId: number;
  capacity: number;
  size: number;
  yearlyPrice: number;
  monthlyPrice: number;
  guaranteeFee: string;
  status: number;
  category: number;
  note?: string | undefined;
  spaceName?: string | undefined;
  pictures?: [];
  groupedAmenities: {
    amenities: {
      attributes:
        | {
            contentType: string;
            content_type: undefined;
            name: string;
            id?: number | undefined;
            content?: string | undefined;
          }[]
        | undefined;
      status: string;
      name: string;
      id?: number | undefined;
    }[];
    name: string;
  }[];
};

export function useSpaceSelection({
  defaultSpaces,
}: {
  defaultSpaces?: { value: string; label: string }[];
}) {
  const [spaces, setSpaces] = useState<{ value: string; label: string }[]>(
    defaultSpaces ?? [],
  );
  const [spacesPO, setSpacesPO] = useState<SpacePO[]>([]);
  const [clonedSpacePO, setClonedSpacePO] = useState<SpacePO>();

  const handleSearchSpaces = useCallback(async (term: string) => {
    const results = await spaceService.searchSpaces({ term });
    setSpaces(
      results.map((space) => ({
        value: space.id.toString(),
        label: space.name,
      })),
    );
  }, []);

  const handleSpaceSelect = useCallback(
    async ({ spaceId }: { spaceId: string }) => {
      const spacePrivateRooms: InventoryPrivateRoomSummary[] =
        await privateOfficeService.listPrivateOfficesInventory({
          spaces: [spaceId],
          skipRecentUpdatesFilter: true,
        });

      const spacePO = spacePrivateRooms.map((privateRoom) => {
        return {
          id: privateRoom.id,
          search_id: privateRoom.search_id || Number(privateRoom.id),
          status: privateRoom.status,
          capacity: privateRoom.capacity,
          spaceId: privateRoom.capacity,
          guaranteeFee: privateRoom.guarantee_fee,
          label: privateRoom.label,
          yearlyPrice: privateRoom.yearly_price,
          monthlyPrice: privateRoom.monthly_price,
          note: privateRoom.notes,
          size: privateRoom.size,
          category: privateRoom.category,
          groupedAmenities: [],
        } as SpacePO;
      });

      setSpacesPO(spacePO);
    },
    [],
  );

  const handleClonePrivateRoom = useCallback(
    async (privateRoomId: string) => {
      const privateRoom = spacesPO.find(
        (privateRoom) => privateRoom.id === privateRoomId,
      );

      const amenities =
        await privateOfficeService.getGroupedAmenitiesForm(privateRoomId);

      if (!privateRoom) {
        throw new Error("Private room not found");
      }
      setClonedSpacePO({
        id: privateRoom.id,
        label: privateRoom.label,
        spaceId: Number(privateRoom.spaceId),
        capacity: privateRoom.capacity,
        size: privateRoom.size,
        yearlyPrice: privateRoom.yearlyPrice,
        monthlyPrice: privateRoom.monthlyPrice,
        guaranteeFee: privateRoom.guaranteeFee,
        status: privateRoom.status,
        category: privateRoom.category,
        note: privateRoom.note,
        pictures: privateRoom.pictures,
        groupedAmenities: amenities.map((group) => ({
          ...group,
          amenities: group.amenities.map((amenity) => ({
            ...amenity,
            attributes: amenity.attributes?.map((attribute) => ({
              ...attribute,
              contentType: attribute.content_type,
              content_type: undefined,
            })),
          })),
        })),
      });
    },
    [setClonedSpacePO, spacesPO],
  );

  return {
    spaces,
    spacesPO,
    clonedSpacePO,
    handleSearchSpaces,
    handleSpaceSelect,
    setClonedSpacePO: handleClonePrivateRoom,
  };
}
